import {AbstractControl, UntypedFormArray, UntypedFormGroup, ValidatorFn, Validators} from '@angular/forms';
import {isFormGroup} from './type-guards';

/**
 * Null-safe value-is-anything check.  If a form group/array is passed in, will return true if any child control has a value.
 *
 * @param c Control to check
 */
export function valueExists(c: AbstractControl | UntypedFormGroup | UntypedFormArray): boolean {
  if(c instanceof UntypedFormGroup || c instanceof UntypedFormArray) {
    for(const field in c.controls) {
      if(c.controls.hasOwnProperty(field) && valueExists(c.controls[field])) {
        return true;
      }
    }
  } else {
    return c && !!c.value;
  }
}

/**
 * Recurses over the controls and clears the errors
 *
 * @param control Parent control to recurse
 */
export function clearErrors(control: AbstractControl | UntypedFormGroup | UntypedFormArray) {
  if(control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
    for(const field in control.controls) {
      if(control.controls.hasOwnProperty(field)) {
        clearErrors(control.controls[field]);
      }
    }
  }
  control?.setErrors(null);
}

export function collectErrors(control: AbstractControl): any | null {
  if(isFormGroup(control)) {
    return Object.entries(control.controls)
      .reduce(
        (acc, [key, childControl]) => {
          const childErrors = collectErrors(childControl);
          if(childErrors) {
            acc = {...acc, [key]: childErrors};
          }
          return acc;
        },
        null
      );
  } else {
    return control?.errors;
  }
}

export function setOptional(...controls: AbstractControl[]) {
  controls?.forEach(control => {
    control.setErrors(null);
    control.clearValidators();
    control.updateValueAndValidity();
  });
}

export function setRequired(...controls: AbstractControl[]) {
  controls?.forEach(control => {
    control.setValidators([Validators.required]);
    control.updateValueAndValidity();
  });
}

/** Replace the validators on a control with the provided ones */
export function setValidators(control: AbstractControl, validators: ValidatorFn | ValidatorFn[]) {
  control.setValidators(validators);
  control.updateValueAndValidity();
}
